import type { EssayData } from "../types";

// Essay content — edit this file to update the essay text.
const essayContent: EssayData = {
  label: "Essay",
  kicker: "Personal Essay · Ten years in",
  title: "The Long Way Around",
  standfirst:
    "A decade in software — two layoffs, an acquisition, a mentor's exit and a founding-engineer leap — and the quiet thread of curiosity that turned out to connect all of it.",
  date: "June 2026",
  readTime: "3 min read",
  cardBlurb:
    "The honest version of how I got here: the detours, the layoffs, the chapter I didn't plan and what a decade of building actually taught me.",
  body: [
    { t: "p", drop: true, text: "I didn't plan this career. Not really." },
    {
      t: "p",
      text: "I planned to get a job. I planned to move to Bangalore, find an apartment, figure out groceries and avoid embarrassing myself in meetings. The rest happened the way most interesting things do: sideways, through detours, under pressure and occasionally by accident.",
    },
    {
      t: "p",
      text: "I started at Meredith Corp fresh out of university, mostly because my professors told me to sit for the interview. I did. I got through it. I remember thinking the offer letter felt like proof of something, though I wasn’t sure what yet. Turns out it was just the beginning of a very long lesson in figuring out what you actually want.",
    },
    {
      t: "p",
      text: "Those early years were spent trying things. Backend. Machine learning. Frontend. I wasn’t strategic about it. I was just curious, and curiosity is a perfectly good compass when you don’t have a map. Frontend stuck. The feedback loop of building something and immediately seeing it work (or spectacularly fail) felt honest. I liked that. More importantly, it was where I stopped thinking of software engineering as a job and started seeing it as something I genuinely wanted to get better at.",
    },
    {
      t: "quote",
      text: "Curiosity is a perfectly good compass when you don't have a map.",
    },
    {
      t: "p",
      text: "Then came Baker Hughes. My first big enterprise. My first experience of architecture reviews, planning rituals and the quiet revelation that engineering is much bigger than writing code. It also gave me my first layoff — our office was dissolved during a restructuring. I was young enough that it felt enormous. I spent a few weeks pretending I was fine and then quietly cracked the interview at Agara during my notice period. Turns out crisis is a decent motivator.",
    },
    {
      t: "p",
      text: "Agara was where things got real. Employee #28. Startup energy, coworking desks, a culture that I didn’t know I needed until I was inside it. My mentor there taught me most of what I know about frontend engineering — and then left to start his own company, as good mentors often do. I stepped into the lead role because someone had to and apparently that someone was me. Terrifying at the time. I’m grateful for it now. Looking back, Agara wasn’t just where I learned leadership. It was where I stopped wondering whether I’d chosen the right profession. For the first time, I felt like I belonged.",
    },
    {
      t: "p",
      text: "Then Coinbase acquired us. I won’t pretend I was thrilled. I was scared — of losing the thing we’d built, the people, the sense that what you built actually mattered. Large companies have their own gravity. You learn different skills: documentation, cross-functional collaboration, how to make impact visible in rooms you’re not always in. It was also where I learned that engineering impact isn’t always proportional to engineering output. Communication, alignment and influence matter more than most engineers realise.",
    },
    {
      t: "p",
      text: "A second layoff — this time at Coinbase. And I’ll say this: you get better at them. Not numb, just better. More pragmatic. Less identity crisis. I landed at LegalZoom, built things I’m proud of, grew into product thinking and learned that the best engineers care not just about how something is built, but why someone needs it in the first place. I also learned something quieter and more personal: that knowing when a chapter is over is its own kind of skill.",
    },
    {
      t: "quote",
      text: "Knowing when a chapter is over is its own kind of skill.",
    },
    {
      t: "p",
      text: "And then I made a decision that probably looked unhinged from the outside: I left without another role lined up.",
    },
    {
      t: "p",
      text: "It was a period of personal grief. I needed to stop. So I stopped.",
    },
    {
      t: "p",
      text: "What happened a few months later, I couldn’t have planned for. A chance conversation with my former CEO from Agara. An idea he was building. An invitation to come build it with him — as a founding engineer.",
    },
    { t: "beat", text: "I said yes." },
    {
      t: "p",
      text: "That’s where I am now. Architecting products and systems from the ground up. Making decisions that actually stick. It is, genuinely, the hardest and most rewarding thing I’ve done — and the clearest proof I have that growth rarely arrives on schedule. It tends to show up in the aftermath of layoffs, in the gap left by a mentor’s departure, in the moment you say yes to something before you feel ready. Most of my best chapters started with some version of *I’m not sure this is a good idea*.",
    },
    { t: "divider" },
    {
      t: "p",
      text: "It took more than a decade, two layoffs, an acquisition and a founding engineer role to figure it out — that first offer letter was proof of something after all. Not that I had made it. Just that I was about to begin.",
    },
  ],
  closing:
    "If you've made it this far and you're thinking about working together — or you just want to swap travel stories — I'd love to hear from you.",
};

export default essayContent;
